import type { InjectionProtocol } from "@src/packages/injection-protocol/types";

/**
 * Which presentation she is holding, decided once, before any walk starts.
 *
 * A second narrowing axis beside `deviceDecision`, and it exists for one
 * medication so far. Menopur's 75 and 150 IU vials are mixed fresh for every
 * injection; its 600 and 1200 IU vials are mixed **once** and drawn from for up
 * to 28 days. Both arrive under the same "powder + solvent" button, so nothing
 * in her record tells them apart — and the two procedures contradict each
 * other at the end, where one says to throw away what is left in the vial and
 * the other says that is the rest of her cycle.
 *
 * It is a pure function for the same reason `deviceDecision` is: this used to
 * be a step in the middle of the walk that told her to "stop here" and then
 * offered "next step", because a step cannot end a walk. The question belongs
 * before the walk, where the code already branches (Anaïs, 2026-09-28).
 *
 * ### Why the second question is answered and not asked
 *
 * Once she has said the vial is the multidose one, "is it already mixed?" has
 * an answer in her own record: a dose logged against **this calendar line**
 * means she has opened it. The caller derives that — see
 * `useOpenedVialEvidence` in `care` — and passes it here, because nothing in
 * this package may reach the API.
 *
 * It is a presumption, never a verdict, and it runs both ways. She may have
 * finished one vial and be opening the next, which looks exactly like a later
 * evening from the outside — so the walk says which evening it assumed and she
 * corrects it in one tap. And she may be on a later evening with nothing
 * logged, which looks exactly like a first one — so the mixing steps carry the
 * same escape, for as long as nothing has been drawn.
 */

export type PresentationDecisionStage =
  /** Ask which vial she has; nothing recorded, and the procedures disagree. */
  | "ask"
  /** She says her vial is none of these. Nothing to walk, and a reason to give. */
  | "unguided"
  /** Nothing to ask; `chosen` is the walk. */
  | "settled";

export type PresentationDecisionInput = {
  /** Every procedure that answers to her medication, already market-filtered. */
  protocols: readonly InjectionProtocol[];
  /**
   * The container size she read off the glass this session, in the protocols'
   * own unit — `600`, not `"multidose"`.
   *
   * A number rather than a vocabulary, because it is what is printed on her
   * vial and because it is what her calendar stores. The two used to be
   * different things kept in step, which is the shape the device question was
   * rebuilt to remove: her answer *is* the stored value.
   */
  answered: number | null;
  /**
   * The size her calendar already holds for this medication, if any.
   *
   * Written when she answered this question on an earlier evening, or typed
   * into her medication form when she added it. Either way it came from her,
   * so a value here removes the question rather than becoming a second one.
   *
   * A size no procedure covers counts as nothing recorded — better to ask than
   * to act on a number we cannot place. `null` also means the field is not
   * readable yet, which is the state until the API carries it.
   *
   * The unit travels with the value and is not optional, because a quantity
   * without one cannot be matched against anything: her calendar can hold
   * `600 mg` where this medication's containers are measured in IU, and a bare
   * `600` would have walked her through the multidose procedure on the
   * strength of a coincidence between two different scales.
   */
  recorded: { value: number; unit: string } | null;
  /**
   * She has told us her vial is none of the sizes offered.
   *
   * The escape the device question has had since the start, and this one did
   * not: four buttons that do not match what is in her hand, and no way past
   * them but the X.
   */
  unlisted: boolean;
  /**
   * Whether a dose was already logged against this calendar line.
   *
   * `null` means the record could not be read — not that there is none. The two
   * are different: an unreadable record must not silently skip the mixing.
   */
  hasEarlierDose: boolean | null;
  /**
   * What she has said about this vial herself, which outranks her record.
   *
   * One value rather than two booleans. "I am opening a new one" and "mine is
   * already mixed" are answers to the same question and cannot both be true,
   * and a pair of flags that can contradict each other is the shape three of
   * the six device bugs had — nothing fails, one of them silently wins.
   *
   * `"already-mixed"` is the escape from inside the walk: the mixing steps are
   * the last moment at which nothing has been drawn, so she may still say the
   * vial was prepared on an earlier evening.
   */
  saidAboutVial: "already-mixed" | "new-vial" | null;
};

export type PresentationDecision = {
  stage: PresentationDecisionStage;
  /** The walks to hand downstream. One once settled; all of them while asking. */
  chosen: readonly InjectionProtocol[];
  /**
   * The container sizes to offer as buttons — deduplicated and ascending.
   *
   * Taken from what the candidate procedures declare, so a market that sells
   * one size offers nothing and a medication with no size declared offers
   * nothing either.
   */
  options: readonly number[];
  /**
   * The unit those sizes are printed in, for the buttons to say `600 IU`.
   *
   * Null exactly when `options` is empty. Read from the protocols rather than
   * held beside the numbers in the component: a label built there would be
   * Menopur's for whichever medication declared a size next.
   */
  optionUnit: string | null;
  /** What the walk assumed about her vial, for the sentence that says so. */
  assumedFirstEvening: boolean;
  /**
   * Her record positively said this vial is open, and the walk acted on it.
   *
   * The only flag a surface may use to claim something about her calendar.
   * `!assumedFirstEvening` is not that flag and reading it as one told every
   * patient on every medication that her calendar showed a recent dose — three
   * of the four returns below leave `assumedFirstEvening` false for reasons
   * that have nothing to do with a record: no presentation to choose between,
   * the question not asked yet, or a fresh-mix vial. Only this one means a read
   * happened and came back positive.
   */
  assumedLaterEvening: boolean;
};

const SIZES_OF = (protocol: InjectionProtocol): readonly number[] =>
  protocol.packageQuantities ?? [];

export function presentationDecision({
  protocols,
  answered,
  recorded,
  unlisted,
  hasEarlierDose,
  saidAboutVial,
}: PresentationDecisionInput): PresentationDecision {
  const sized = protocols.filter((protocol) => SIZES_OF(protocol).length > 0);

  // The ordinary case, and every medication but one: no procedure declares a
  // container size, so there is nothing to choose between.
  if (sized.length === 0) {
    return {
      stage: "settled",
      chosen: protocols,
      options: [],
      optionUnit: null,
      assumedFirstEvening: false,
      assumedLaterEvening: false,
    };
  }

  const options = [
    ...new Set(sized.flatMap((protocol) => SIZES_OF(protocol))),
  ].sort((a, b) => a - b);
  const optionUnit = sized[0]?.packageQuantityUnit ?? null;

  /**
   * The walk for a size, and which evening of it.
   *
   * Two axes, resolved in order: the size says which vial, and where that vial
   * has two procedures the evening says which of them.
   */
  const walkFor = (size: number): PresentationDecision => {
    const matching = sized.filter((protocol) =>
      SIZES_OF(protocol).includes(size),
    );
    const first = matching.filter((p) => p.presentation === "multidose-first");
    const later = matching.filter((p) => p.presentation === "multidose-later");

    // A size with one procedure: the fresh vial, and every size a medication
    // gains from here that is mixed for each dose.
    if (first.length === 0 || later.length === 0) {
      return {
        stage: "settled",
        chosen: matching,
        options,
        optionUnit,
        assumedFirstEvening: false,
        assumedLaterEvening: false,
      };
    }

    // `true` is the only *recorded* value that skips the mixing: `null` — a
    // record we could not read — must not, because mixing a mixed vial wastes
    // one, and skipping the mixing of an unmixed one means injecting powder.
    //
    // That asymmetry is only safe because she can say so herself. Without the
    // escape it strands every patient who does not log her doses on the mixing
    // steps of a vial she mixed days ago: she has one pre-filled solvent
    // syringe per pack, so on a later evening there is nothing to follow those
    // steps with (Anaïs, 2026-09-30).
    const recordSaysOpen =
      hasEarlierDose === true && saidAboutVial !== "new-vial";
    const onALaterEvening = saidAboutVial === "already-mixed" || recordSaysOpen;

    return {
      stage: "settled",
      chosen: onALaterEvening ? later : first,
      options,
      optionUnit,
      assumedFirstEvening: !onALaterEvening,
      // A positive read and nothing else. This flag is what licenses a surface
      // to tell her what her calendar shows, and when she says the vial is
      // already mixed from inside the walk, her calendar has said nothing.
      assumedLaterEvening: saidAboutVial === null && recordSaysOpen,
    };
  };

  const stuck = (stage: PresentationDecisionStage): PresentationDecision => ({
    stage,
    chosen: stage === "unguided" ? [] : protocols,
    options,
    optionUnit,
    assumedFirstEvening: false,
    assumedLaterEvening: false,
  });

  // She has said her vial is none of these. Nothing narrows, and the honest
  // outcome is a card that says why rather than a walk written for a container
  // she does not have.
  if (unlisted) return stuck("unguided");

  // Her own answer outranks everything, including her record: she is looking at
  // the glass and the record was typed on another evening.
  if (answered !== null && options.includes(answered)) return walkFor(answered);

  // A size we cannot place counts as nothing recorded. Better to ask than to
  // act on a number no procedure here was written for.
  // Both halves have to agree. A number that matches on a unit this medication
  // is not measured in is a coincidence, not a record.
  const onFile =
    recorded !== null &&
    recorded.unit === optionUnit &&
    options.includes(recorded.value)
      ? recorded.value
      : null;

  // A medication sold in one size where she is has nothing to ask. Nothing in
  // the catalogue is in that shape today — the US walk declares no sizes at all
  // and leaves two branches earlier — so this is the rule stated rather than a
  // path anything currently takes.
  const only = options.length === 1 ? options[0] : null;
  if (only !== undefined && only !== null) return walkFor(only);

  // **The rule.** A size on file is used as she gave it — she typed it when
  // she added the medication, or the coach wrote it back the first time she
  // answered, and there is no third way for one to appear. So it is not put
  // back to her every evening; what carries the check is the walk itself,
  // which names the vial it was written for and says where to correct the
  // record (Anaïs, 2026-10-01).
  if (onFile === null) return stuck("ask");
  return walkFor(onFile);
}
